import { cn } from '@/lib/utils'

/**
 * Shared look for every text-entry field (Input, Textarea, PriceInput, …) so they
 * read as one family with the Select trigger.
 *
 * - White field, 16px text, sand-300 border at rest that darkens on hover.
 * - Placeholder is muted (not faint — faint fails contrast), upright, 16px.
 * - No focus ring and no colour swap on focus (product decision, see the top of
 *   `styles/globals.css`). Fields carry `data-field` so the global focus reset
 *   leaves their resting border alone instead of lightening it.
 */
export const fieldBaseClasses = cn(
  'w-full bg-clovie-input text-[16px] text-clovie-text',
  'border border-clovie-border-strong rounded-[var(--r-md)]',
  'placeholder:text-clovie-muted placeholder:not-italic placeholder:text-[16px]',
  'transition-colors duration-150 focus:outline-none',
  'hover:border-[var(--sand-400)]',
  // A read-only field doesn't darken on hover, so it doesn't look editable. Keyed on the `readonly`
  // attribute (`:read-only` also matches disabled fields and buttons) and skipped for disabled or
  // invalid fields, whose own border must win.
  '[&[readonly]:not(:disabled):not([aria-invalid=true]):hover]:border-clovie-border-strong',
  // Callers that render their own error line mark the field with aria-invalid only.
  'aria-[invalid=true]:border-form-error aria-[invalid=true]:hover:border-form-error',
  'disabled:cursor-not-allowed disabled:bg-clovie-sunk disabled:text-clovie-muted disabled:border-clovie-border'
)

/** Error border; also pins hover/focus so the field stays visibly in error. */
export const fieldErrorClasses = 'border-form-error hover:border-form-error focus:border-form-error'

/** Label above a field: 16px medium, secondary ink. */
export const labelStyles = cn(
  'block text-[16px] font-medium text-clovie-text-2 mb-1.5',
  'peer-disabled:opacity-50'
)

/** Helper/description text under a field. */
export const helperTextStyles = 'mt-1.5 text-[16px] text-clovie-muted'

/**
 * Error text under a field. Uses `form-error-text` (blush-600, about 4.6:1 on white)
 * because the `form-error` border colour is too light for text. Keep error text on white surfaces.
 */
export const errorTextStyles = 'mt-1.5 text-[16px] text-form-error-text'

/** Links a field to its error (preferred) or helper text, plus any extra ids. */
export function fieldDescribedBy(
  fieldId: string,
  { error, helperText, extra }: { error?: string; helperText?: string; extra?: string }
): string | undefined {
  const own = error ? `${fieldId}-error` : helperText ? `${fieldId}-helper` : null
  return [own, extra].filter(Boolean).join(' ') || undefined
}
